import { WSInfoBox } from "@wingspanhq/fe-component-library";
import {
  IMember,
  MemberWithholdingStatus
} from "@wingspanhq/users/dist/lib/interfaces";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { getVerificationMissingData } from "../../../services/api/onboarding/missingFields";
import type {
  bankingApiSpec,
  onboardingApiSpec
} from "@wingspanhq/wingspan-api-types";
import { selectorTaxWithholdingAccount } from "../../../shared/selectors/selectorTaxWithholdingAccount";
import { selectorCanEnableTaxWithholdings } from "../../Taxes/selectors/selectorCanEnableTaxWithholdings";
import { getRequireAdditionalInformation } from "../components/FormCreateInternalAccount/utils";
import { CalculateTaxRate } from "../steps/TaxWithholdings/CalculateTaxRate";
import { ConfirmAndSave } from "../steps/TaxWithholdings/ConfirmAndSave";
import { ContractorTaxes } from "../steps/TaxWithholdings/ContractorTaxes";
import { TaxWithholdingsContext } from "../steps/TaxWithholdings/types";
import {
  OnboardingContext,
  OnboardingModuleTaxWithholdings,
  OnboardingStep,
  OnboardingStepStatus
} from "../types";

const getStatus = (
  member?: IMember,
  internalAccounts?: bankingApiSpec.InternalAccount[],
  context?: TaxWithholdingsContext,
  bankingMissingData?: Awaited<ReturnType<typeof getVerificationMissingData>>,
  featureFlags?: ReturnType<typeof useFeatureFlags>["data"]
): OnboardingStepStatus => {
  if (
    member?.profile.withholdings?.tax?.status === MemberWithholdingStatus.Active
  ) {
    const taxWithholdingInternalAccount = (internalAccounts || []).find(
      selectorTaxWithholdingAccount
    );

    if (context?.taxWithholdingsSkipped) {
      return "Pending";
    }

    if (!taxWithholdingInternalAccount) {
      return "None";
    }

    if (taxWithholdingInternalAccount.status === "Pending") {
      const requireAdditionalInformation = getRequireAdditionalInformation(
        taxWithholdingInternalAccount,
        bankingMissingData,
        featureFlags
      );

      if (requireAdditionalInformation) {
        // Keep going with onboarding, we will ask for additional info
        return "None";
      }

      return "Pending";
    }

    return "Complete";
  }

  if (context?.taxWithholdingsSkipped) {
    return "Pending";
  }

  return "None";
};

export const getTaxWithholdingsStepConfig = (
  onboardingModule: OnboardingModuleTaxWithholdings,
  context: OnboardingContext,
  monitoredEntity?: onboardingApiSpec.CustomerEntity,
  member?: IMember,
  internalAccounts?: bankingApiSpec.InternalAccount[],
  bankingMissingData?: Awaited<ReturnType<typeof getVerificationMissingData>>,
  featureFlags?: ReturnType<typeof useFeatureFlags>["data"]
): OnboardingStep | null => {
  if (monitoredEntity) {
    const canEnableTaxWithholdings = selectorCanEnableTaxWithholdings(
      monitoredEntity,
      member
    );

    if (!canEnableTaxWithholdings) {
      return null;
    }
  }

  if (!!context?.accountType?.country && context.accountType.country !== "US") {
    return null;
  }

  const slug = "tax-withholdings";

  const status = getStatus(
    member,
    internalAccounts,
    context,
    bankingMissingData,
    featureFlags
  );

  return {
    title: "Tax withholdings",
    slug,

    steps: onboardingModule.hideIntro
      ? [
          {
            title: "Calculate tax rate",
            slug: "calculate-tax-rate",
            component: CalculateTaxRate
          },
          {
            title: "Confirm & save",
            slug: "confirm",
            component: ConfirmAndSave
          }
        ]
      : [
          {
            title: "Contractor taxes",
            slug: "contractor-taxes",
            component: ContractorTaxes
          },
          {
            title: "Calculate tax rate",
            slug: "calculate-tax-rate",
            component: CalculateTaxRate
          },
          {
            title: "Confirm & save",
            slug: "confirm",
            component: ConfirmAndSave
          }
        ],

    status,
    result: {
      Complete: {
        title: "Your tax withholdings is activated",
        description:
          "Congrats! You’re set up to set aside tax withholdings so you are financially prepared for taxes. You can view your tax withholdings from your “Financial benefits” section. Click continue for the next step."
      },
      Pending: context?.taxWithholdingsSkipped
        ? {
            type: "tasks",
            title: "No problem!",
            description: (
              <WSInfoBox transparent>
                <p>
                  You’ve opted out of tax withholdings for now. Here’s what to
                  remember:
                </p>
                <ul>
                  <li>Quarterly deadlines: Apr 15, Jun 15, Sep 15, Jan 15</li>
                  <li>Set aside 25-30% of income for taxes</li>
                  <li>Use IRS Form 1040-ES for payments</li>
                  <li>Keep detailed income records</li>
                </ul>
                <p>
                  You can enable tax withholding anytime under your “Financial
                  benefits” section. We'll also show a reminder on your
                  invoices.
                </p>
              </WSInfoBox>
            )
          }
        : {
            title: "Review in progress",
            description:
              "Your information was received and our team is currently reviewing it. Once the information is approved, features such as Wingspan Wallet and Tax Withholdings can be activated. The review process typically takes less than one (1) business day. Thank you for your patience. Click continue for the next step."
          }
    }
  };
};
